import React from "react";
import { Link } from "react-router-dom";
import LandingDemo from "../components/LandingDemo";

export default function Landing() {
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-6" data-testid="landing-eyebrow">
            [01] Performance Alignment Portal
          </div>
          <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight" data-testid="landing-headline">
            Honest calibration.<br />
            <span className="text-[#FF4F00]">Zero bias</span> loops.
          </h1>
          <p className="mt-8 text-lg max-w-2xl leading-relaxed text-[#333]" data-testid="landing-sub">
            Upload a Position Description — or let our AI <b>write one for you from scratch</b>. We extract the actual tasks. Employee and manager each rate in private — no peeking.
            When both sides submit, the vault unlocks and the overlay reveals the real conversation.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register" className="cal-btn" data-testid="cta-get-started">Start a calibration →</Link>
            <Link to="/pricing" className="cal-btn cal-btn-ghost" data-testid="cta-pricing">See pricing</Link>
            <Link to="/login" className="cal-btn cal-btn-ghost" data-testid="cta-signin">I have an account</Link>
          </div>
        </div>
        <aside className="lg:col-span-4">
          <div className="cal-card cal-shadow p-6 h-full">
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">Protocol</div>
            <ol className="mt-4 space-y-4">
              {[
                ["01", "Draft the PD with our AI generator — or upload your existing one"],
                ["02", "AI extracts measurable tasks & KPIs"],
                ["03", "Employee adds their goals"],
                ["04", "Employee and manager rate — blind"],
                ["05", "Overlay unlocks. AI analyses gaps"],
                ["06", "AI coaches manager on discussion areas"],
                ["07", "Conversation begins"],
              ].map(([n, t]) => (
                <li key={n} className="flex gap-3">
                  <span className="font-display font-bold text-2xl text-[#FF4F00] leading-none">{n}</span>
                  <span className="text-sm leading-relaxed text-[#111]">{t}</span>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </div>

      <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { k: "PD GENERATOR", t: "AI drafts a full Position Description from a role title + 4-5 discovery questions. Ready in ~30 seconds." },
          { k: "BLIND", t: "Neither party sees the other's rating until both submit." },
          { k: "STRUCTURED", t: "Every task maps directly to the PD — no vague goals." },
          { k: "ACTIONABLE", t: "AI-suggested talking points for each perception gap." },
        ].map((x) => (
          <div key={x.k} className="cal-card p-6" data-testid={`feature-${x.k.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00]">{x.k}</div>
            <div className="mt-3 text-base leading-relaxed">{x.t}</div>
          </div>
        ))}
      </div>

      {/* Long-form SEO content. Real prose Google can rank. */}
      <section className="mt-24 max-w-3xl" aria-labelledby="seo-features">
        <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-4">[02] What CALIBRATE does</div>
        <h2 id="seo-features" className="font-display font-bold text-4xl tracking-tight mb-6">
          Performance calibration software, without the bias loops.
        </h2>
        <div className="space-y-5 text-[#222] leading-relaxed">
          <p>
            CALIBRATE is a multi-tenant performance review portal built for managers, HR teams, and
            small-to-mid business owners running formal calibration conversations. Upload a Position
            Description PDF — or generate one from scratch with our AI PD Generator — and Anthropic's
            Claude extracts the actual responsibilities and KPIs you're holding the role to. No more
            reviewing performance against "vibes."
          </p>
          <p>
            Don't have a PD yet? The built-in <b>PD Generator</b> asks 4-5 targeted discovery questions
            about the role, then drafts a full ~800-word Position Description covering position overview,
            key responsibilities, KPIs, required skills, and stakeholder relationships. Copy it into your
            HRIS or feed it straight back into a calibration.
          </p>
          <p>
            Each calibration is two-sided and time-boxed. The employee opens a single-use magic-link
            URL (no account required) and rates themselves on every task. The manager rates in parallel.
            Neither side can see the other's scores until both have submitted. Once they do, the
            comparison overlay unlocks — radar chart, bar comparison, gap analysis, and AI-suggested
            conversation starters for the largest perception gaps.
          </p>
        </div>
      </section>

      <LandingDemo />

      <section className="mt-20 max-w-3xl" aria-labelledby="seo-faq">
        <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-4">[04] Frequently asked</div>
        <h2 id="seo-faq" className="font-display font-bold text-4xl tracking-tight mb-4">
          Got questions about calibration?
        </h2>
        <p className="text-[#444] leading-relaxed mb-6 text-lg">
          We cover the most common ones — what calibration is, how the blind workflow removes bias,
          what the AI actually does, data isolation, and pricing — in one place.
        </p>
        <Link to="/faq" className="cal-btn" data-testid="landing-faq-link">
          Read the FAQ →
        </Link>
      </section>

      <footer className="mt-24 pt-10 border-t border-[#111]/15 flex flex-wrap items-center justify-between gap-4 text-xs font-mono-cal text-[#666]">
        <div>© {new Date().getFullYear()} CALIBRATE · Performance alignment</div>
        <div className="flex flex-wrap gap-5">
          <Link to="/pricing" className="hover:text-[#FF4F00]" data-testid="footer-pricing-link">Pricing</Link>
          <Link to="/faq" className="hover:text-[#FF4F00]" data-testid="footer-faq-link">FAQ</Link>
          <Link to="/blog" className="hover:text-[#FF4F00]" data-testid="footer-blog-link">Blog</Link>
          <Link to="/terms" className="hover:text-[#FF4F00]" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
          <a href="mailto:support@calibrate.management" className="hover:text-[#FF4F00]">Contact</a>
        </div>
      </footer>
    </div>
  );
}
